import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { TRAINING_URL } from "../shared/src/lib/shared";
import { ErrorHandlerService } from "./error-handler.service";

@Injectable({
    providedIn: 'root',
})
export class TrainingQuizService {

    private readonly TRAINING_QUIZ_URL = `${TRAINING_URL}`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    downloadTemplate(): Observable<Blob> {
        return this.http.get(`${this.TRAINING_QUIZ_URL}/quizMaster/questions/downloadExcelTemplate`, { responseType: 'blob' });
    }

    //get all quiz
    getAllQuiz(
        pageIndex: number,
        pageSize: number,
        trainingTypeIds: number[],
        countryIds?: number[],
        isProductBased?: boolean,
        productId?: number[]
    ): Observable<any> {
        let params = new HttpParams();
        if (trainingTypeIds.length > 0) {
            params = params.set('trainingTypeId', trainingTypeIds.join(','));
        }
        if (countryIds != null) {
            const ids = Array.isArray(countryIds) ? countryIds : [countryIds];
            const cleaned = ids
                .map((id) => Number(id))
                .filter((id) => !Number.isNaN(id) && id > 0);

            if (cleaned.length > 0) {
                // Backend expects `countryId` to be a comma-separated list (ParseArrayPipe)
                params = params.set('countryId', cleaned.join(','));
            }
        }
        if (isProductBased !== undefined && isProductBased !== null) {
            params = params.set('isProductBased', isProductBased.toString());
        }
        if (productId && productId.length > 0) {
            params = params.set('productId', productId.join(','));
        }
        return this.http.get<any>(`${this.TRAINING_QUIZ_URL}/quizMaster/getAll?pageIndex=${pageIndex}&pageSize=${pageSize}`, { params });
    }

    //get all training type (supports single or multiple countryIds)
    getAllTrainingType(searchTerm?: string, countryId?: number | number[]): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        if (countryId !== undefined && countryId !== null) {
            const value = Array.isArray(countryId)
                ? countryId.join(',')
                : countryId.toString();
            if (value) {
                params = params.set('countryId', value);
            }
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingType/getAll`, { params });
    }

    //get all duration (supports single or multiple countryIds)
    getAllDuration(searchTerm?: string, countryId?: number | number[]): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        if (countryId !== undefined && countryId !== null) {
            const value = Array.isArray(countryId)
                ? countryId.join(',')
                : countryId.toString();
            if (value) {
                params = params.set('countryId', value);
            }
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingDuration/getAll`, { params });
    }

    //get all quiz duration (filter by country, sorted by milliseconds ASC by backend)
    getAllQuizDuration(searchTerm?: string, countryId?: number): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        if (countryId && countryId > 0) {
            params = params.set('countryId', countryId.toString());
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingDuration/getAllQuiz`, { params });
    }

    //get all level
    getAllLevel(searchTerm?: string): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingLevel/getAll`, { params });
    }

    //get all test type
    getAllTestType(searchTerm?: string): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingTestType/getAll`, { params });
    }

    //get all status
    getAllStatus(searchTerm?: string): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingStatus/getAll`, { params });
    }

    //get all modules (countryIds: multi-select filter by countries)
    getAllModules(searchTerm?: string, countryIds?: number[], trainingTypeId?: number): Observable<any> {
        let params = new HttpParams();
        if (searchTerm) {
            params = params.set('search', searchTerm);
        }
        if (countryIds && countryIds.length > 0) {
            params = params.set('countryIds', countryIds.join(','));
        }
        if (trainingTypeId) {
            params = params.set('trainingTypeId', trainingTypeId.toString());
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingModule/getAll`, { params });
    }

    //get all modules by training type id
    getAllModulesByTrainingTypeId(trainingTypeId: number): Observable<any> {
        return this.http.get(`${this.TRAINING_QUIZ_URL}/trainingModule/ByTrainingType/${trainingTypeId}`);
    }

    //create new quiz
    createNewQuiz(quizData: any): Observable<any> {
        return this.http.post(`${this.TRAINING_QUIZ_URL}/quizMaster/create`, quizData);
    }

    //get quiz Questions and answers by id
    getQuizById(quizId: number): Observable<any> {
        return this.http.get(`${this.TRAINING_QUIZ_URL}/quizMaster/getQuestionsByQuizId/${quizId}`);
    }

    getQuizByIds(quizIds: number[]): Observable<any> {
        let params = new HttpParams();
        if (quizIds && quizIds.length > 0) {
            params = params.set('quizIds', quizIds.join(','));
        }

        return this.http.get(`${this.TRAINING_QUIZ_URL}/quizMaster/getQuestionsByQuizIds`, { params });
    }

    //Update existing quiz
    updateQuiz(quizId: number, quizData: any): Observable<any> {
        return this.http.put(`${this.TRAINING_QUIZ_URL}/quizMaster/update/${quizId}`, quizData);
    }

    //update status
    updateStatus(id: number, status: boolean): Observable<any> {
        return this.http.put(`${this.TRAINING_QUIZ_URL}/quizMaster/updateStatus/${id}`, { status });
    }

    //get all quiz by training type id
    getAllQuizByTrainingTypeId(pageIndex: number, pageSize: number, trainingTypeIds: number[], status?: boolean, countryIds?: number[]): Observable<any> {
        let params = new HttpParams();
        if (trainingTypeIds.length > 0) {
            params = params.set('trainingTypeId', trainingTypeIds.join(','));
        }
        if (status !== undefined && status !== null) {
            params = params.set('status', status.toString());
        }
        if (countryIds && countryIds.length > 0) {
            params = params.set('countryId', countryIds.join(','));
        }
        return this.http.get(`${this.TRAINING_QUIZ_URL}/quizMaster/getAllByTrainingTypeId?pageIndex=${pageIndex}&pageSize=${pageSize}`, { params });
    }

    //delete quiz
    deleteQuiz(id: number): Observable<any> {
        return this.http.delete(`${this.TRAINING_QUIZ_URL}/quizMaster/delete/${id}`);
    }

    // Training Type CRUD operations
    //create training type
    createTrainingType(trainingTypeData: { name: string; countryIds: number[]; isProductBased?: boolean }): Observable<any> {
        return this.http.post(`${this.TRAINING_QUIZ_URL}/trainingType/create`, trainingTypeData);
    }

    //update training type
    updateTrainingType(id: number, trainingTypeData: { name: string; countryIds: number[]; isProductBased?: boolean }): Observable<any> {
        return this.http.put(`${this.TRAINING_QUIZ_URL}/trainingType/update/${id}`, trainingTypeData);
    }

    //delete training type
    deleteTrainingType(id: number): Observable<any> {
        return this.http.delete(`${this.TRAINING_QUIZ_URL}/trainingType/delete/${id}`);
    }

    // Training Duration CRUD operations (web) - supports single or multiple countries
    createTrainingDuration(payload: { name: string; milliseconds: number; countryIds?: number[]; countryId?: number }): Observable<any> {
        return this.http.post(`${this.TRAINING_QUIZ_URL}/trainingDuration/create`, payload);
    }

    updateTrainingDuration(id: number, payload: { name: string; milliseconds: number; countryIds?: number[]; countryId?: number }): Observable<any> {
        return this.http.put(`${this.TRAINING_QUIZ_URL}/trainingDuration/update/${id}`, payload);
    }

    deleteTrainingDuration(id: number): Observable<any> {
        return this.http.delete(`${this.TRAINING_QUIZ_URL}/trainingDuration/delete/${id}`);
    }

    // Training Module CRUD operations (supports single or multiple countries)
    createTrainingModule(payload: { name: string; trainingTypeId: number; countryIds?: number[]; countryId?: number }): Observable<any> {
        return this.http.post(`${this.TRAINING_QUIZ_URL}/trainingModule/create`, payload);
    }

    updateTrainingModule(id: number, payload: { name: string; trainingTypeId: number; countryIds?: number[]; countryId?: number }): Observable<any> {
        return this.http.put(`${this.TRAINING_QUIZ_URL}/trainingModule/update/${id}`, payload);
    }

    deleteTrainingModule(id: number): Observable<any> {
        return this.http.delete(`${this.TRAINING_QUIZ_URL}/trainingModule/delete/${id}`);
    }

    importQuizFromExcel(formData: any): Observable<any> {
        return this.http.post(`${this.TRAINING_QUIZ_URL}/quizMaster/importFromExcel`, formData);
    }
}